import { api } from "../api";
import { h } from "../ui/dom";
import { errorBanner, loadingBlock } from "./shared/blocks";
import { parseClients, parsePage } from "./fleetModel";
import { safeMessage, tidy } from "./shared/safe";

type Job = { id?: unknown; status?: unknown; build_record_id?: unknown; created_at?: unknown; last_error?: unknown };
type Connection = { id?: unknown; platform?: unknown; status?: unknown; identity?: unknown };
type AuditEntry = { id?: unknown; action?: unknown; actor?: unknown; clientId?: unknown; entityType?: unknown; createdAt?: unknown };

function card(title: string, value: string, detail: string, href: string, action: string, tone = "") {
  return h("article", { class: `ov-card ${tone}`.trim() },
    h("div", { class: "ov-card__label" }, title), h("strong", { class: "ov-card__value" }, value),
    h("p", { class: "ov-card__detail" }, detail), h("a", { class: "btn secondary", href }, action));
}

export async function overviewView(root: HTMLElement): Promise<void> {
  const shell = h("section", { class: "qf cp-overview" });
  root.replaceChildren(shell);
  shell.append(h("div", { class: "topbar" }, h("div", null, h("h1", null, "Overview"), h("p", { class: "muted" }, "A current view of client review, publications, connections and recent work.")), h("a", { class: "btn", href: "#/clients/new" }, "Add client")));
  const summary = h("div", { class: "ov-grid", "aria-label": "Workspace overview" });
  const activity = h("section", { class: "ov-activity", "aria-labelledby": "ov-activity-title" }, h("h2", { id: "ov-activity-title" }, "Recent activity"), loadingBlock("Loading recent activity", 3));
  shell.append(summary, activity);

  const safeLoad = async <T,>(label: string, request: Promise<T>): Promise<T | null> => {
    try { return await request; } catch (e) { summary.append(h("div", { class: "ov-load-error" }, errorBanner(`${label} is unavailable: ${safeMessage(e instanceof Error ? e.message : e, 140)}`))); return null; }
  };
  const [clientsReply, failedReply, deadReply, connectionsReply, auditReply] = await Promise.all([
    safeLoad("Client review totals", (async () => {
      const all: unknown[] = [];
      let offset = 0;
      for (let i = 0; i < 100; i += 1) {
        const reply = await api<unknown>(`/v1/clients?limit=200&offset=${offset}`);
        const parsed = parseClients(reply);
        if (!parsed) throw new Error("The server sent a client list this page does not understand.");
        all.push(...parsed.clients);
        const page = parsePage(reply);
        if (!page || !page.hasMore) return { clients: all };
        if (!page.returned) throw new Error("The client list stopped before all pages were read.");
        offset += page.returned;
      }
      throw new Error("The client list exceeded the supported page count.");
    })()),
    safeLoad("Failed publication totals", api<{ jobs?: Job[] }>("/v1/jobs?status=failed")),
    safeLoad("Failed publication totals", api<{ jobs?: Job[] }>("/v1/jobs?status=dead")),
    safeLoad("Connection status", api<{ connections?: Connection[] }>("/v1/connections")),
    safeLoad("Recent activity", api<{ entries?: AuditEntry[] }>("/v1/audit?limit=8")),
  ]);
  const clients = parseClients(clientsReply);
  if (clients) {
    const awaiting = clients.clients.reduce((n, c) => n + c.reviewWaiting, 0);
    summary.append(card("Awaiting review", String(awaiting), "Client items currently marked for review.", "#/clients/needs-attention", "Review clients", awaiting ? "is-warn" : ""));
  }
  if (failedReply && deadReply) {
    const failed = [...(failedReply.jobs ?? []), ...(deadReply.jobs ?? [])].filter((j) => j.build_record_id != null);
    const capped = (failedReply.jobs?.length ?? 0) >= 200 || (deadReply.jobs?.length ?? 0) >= 200;
    summary.append(card("Failed publications", `${capped ? "At least " : ""}${failed.length}`, "Template publication jobs recorded as failed or dead.", "#/queue", "Open job queue", failed.length ? "is-bad" : ""));
  }
  if (connectionsReply?.connections) {
    const issues = connectionsReply.connections.filter((c) => c.status !== "connected");
    summary.append(card("Connection issues", String(issues.length), "Saved connections not currently marked connected.", "#/connections", "Review connections", issues.length ? "is-warn" : ""));
  }
  if (!summary.children.length) summary.append(errorBanner("Overview data is unavailable. Try again after the API is reachable.", { retry: () => void overviewView(root) }));

  if (!auditReply?.entries) activity.replaceChildren(h("h2", { id: "ov-activity-title" }, "Recent activity"), h("p", { class: "muted" }, "Activity could not be loaded."));
  else if (!auditReply.entries.length) activity.replaceChildren(h("h2", { id: "ov-activity-title" }, "Recent activity"), h("p", { class: "muted" }, "No activity has been recorded yet."));
  else activity.replaceChildren(
    h("h2", { id: "ov-activity-title" }, "Recent activity"),
    h("ol", { class: "ov-activity__list" }, ...auditReply.entries.map((entry) => {
      const when = typeof entry.createdAt === "number" ? new Date(entry.createdAt * 1000).toLocaleString() : "Time unavailable";
      const client = clients?.clients.find((c) => c.id === entry.clientId);
      return h("li", null, h("span", { class: "ov-activity__action" }, tidy(entry.action, 100)), h("span", { class: "muted" }, `${client?.name ?? "Workspace"} · ${tidy(entry.actor ?? "system", 80)} · ${when}`));
    })),
    h("a", { href: "#/reports", class: "ov-more" }, "View reports →"),
  );
}
